import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const runCleanup = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/campaigns/maintenance/cleanup-orphan-tracked');
      const fs = data.found_stories_before ?? 0;
      const fp = data.found_posts_before ?? 0;
      const fc = data.found_completed_stories_no_keywords_before ?? 0;
      const dos = data.deleted_orphan_stories ?? 0;
      const dop = data.deleted_orphan_posts ?? data.deleted_posts ?? 0;
      const dck = data.deleted_completed_stories_no_keywords ?? 0;
      const msg =
        fs === 0 && fp === 0 && fc === 0
          ? (data.message ||
            'Silinecek kayıt yoktu (yetim satır ve tamamlanmış kampanyada kelimesiz hikâye).')
          : `Tespit: yetim ${fs} hikâye, ${fp} gönderi; tamamlanmışta kelimesiz ${fc} hikâye. Silinen: ${data.deleted_stories} hikâye (${dos} yetim + ${dck} tamamlanmış/kelimesiz), ${dop} gönderi.`;
      setSnack({
        open: true,
        message: msg,
        severity: 'success'
      });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e.response?.data?.message || 'Temizlik başarısız',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Ayarlar
      </Typography>
      <Card variant="outlined" sx={{ maxWidth: 640 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Veritabanı bakımı
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Yetim <code>tracked_stories</code> / <code>tracked_posts</code> (kampanya–influencer
            eşleşmesi yok) silinir. Ayrıca <strong>tamamlanmış</strong> kampanyalarda hedef kelime
            tespiti olmayan <code>tracked_stories</code> kayıtları silinir.
          </Typography>
          <Button
            variant="contained"
            color="warning"
            disabled={!isAdmin || loading}
            onClick={() => setConfirmOpen(true)}
          >
            Tespit kayıtlarını temizle
          </Button>
          {!isAdmin && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Bu işlem yalnızca yönetici hesapları içindir.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !loading && setConfirmOpen(false)}>
        <DialogTitle>Tespit kayıtları silinsin mi?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Yetim kayıtlar ve tamamlanmış kampanyada kelimesiz hikâyeler kalıcı olarak silinir. Bu işlem
            geri alınamaz. Devam etmek istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            İptal
          </Button>
          <Button
            onClick={() => void runCleanup()}
            color="warning"
            variant="contained"
            disabled={loading}
          >
            {loading ? '…' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
